import { InfoCircleIcon, LinkBrokenBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Input, LabelWithHint, overlayStyle, Stack, Switch, Tooltip, Uploader } from '@prophecy/ui-v3';
import React from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';

import { StyledHeading } from '../../config/styled';
import { ScrollableStack } from './styled';
import { ComponentProps } from './types';
import { UserAttribute } from './UserAttribute';
import { UserPrompt } from './UserPrompt';

const StyledInputBox = styled(Input)`
  flex: 1;
`;
const StyledUploader = styled(Uploader)`
  margin: 0;
  padding: 0;
  > div {
    justify-content: flex-start;
  }
`;

export default function LdapAuth({ control, errors, updateUserMatchersData, userMatchers }: ComponentProps) {
  const uploadFiles = (files: Blob[], onChange: (e: unknown) => void) => {
    let reader = new FileReader();
    const uploadedFile = files[0];
    reader.readAsText(uploadedFile);
    reader.onloadend = () => {
      onChange(reader.result);
    };
  };

  return (
    <ScrollableStack gap={theme.spaces.x24}>
      <Controller
        render={({ field }) => (
          <UserPrompt
            hasPrompt={true}
            errorMsg={errors?.ldap?.usernamePrompt?.message}
            value={field.value}
            onChange={field.onChange}
          />
        )}
        control={control}
        name='ldap.usernamePrompt'
      />
      <Controller
        render={({ field }) => (
          <StyledInputBox
            label={
              <Stack gap={theme.spaces.x4} direction='horizontal'>
                * Host
                <Tooltip
                  overlayStyle={overlayStyle}
                  title='Host and optional port of the LDAP server in the form "host:port". If the port is not supplied, it will be guessed based on "Disable SSL", and "Start TLS" flags. 389 for insecure or “Start TLS” connections, 636 otherwise.'>
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            }
            placeholder='a.b.c.d:636'
            {...field}
            variant={errors?.ldap?.host ? 'error' : 'primary'}
            helpText={errors?.ldap?.ssoURL?.message}
            data-test-id='ldap-auth-host-input'
          />
        )}
        control={control}
        name='ldap.host'
        rules={{
          required: 'Host is required'
        }}
      />
      <Stack direction='horizontal' gap={theme.spaces.x12} align='space-between'>
        <Controller
          render={({ field }) => (
            <Stack gap={theme.spaces.x4} direction='horizontal' alignY='center'>
              <Switch {...field} checked={Boolean(field.value)} data-test-id='ldap-auth-disable-ssl-switch'>
                Disable SSL
              </Switch>
              <Tooltip
                overlayStyle={overlayStyle}
                title='Following field is required if the LDAP host is not using TLS (port 389). Because this option inherently leaks passwords to anyone on the same network.'>
                <div>
                  <InfoCircleIcon type='default' color={theme.colors.gray500} />
                </div>
              </Tooltip>
            </Stack>
          )}
          control={control}
          name='ldap.insecureNoSSL'
        />
        <Controller
          render={({ field }) => (
            <Stack gap={theme.spaces.x4} direction='horizontal' alignY='center'>
              <Switch
                {...field}
                checked={Boolean(field.value)}
                data-test-id='ldap-auth-insecure-skip-certificate-verification-switch'>
                Skip Certificate Verification
              </Switch>
              <Tooltip
                overlayStyle={overlayStyle}
                title='If a custom certificate is not provided, this option can be used to turn on TLS certificate checks. As noted, it is insecure and should not be used outside of explorative phases.'>
                <div>
                  <InfoCircleIcon type='default' color={theme.colors.gray500} />
                </div>
              </Tooltip>
            </Stack>
          )}
          control={control}
          name='ldap.insecureSkipVerify'
        />
        <Controller
          render={({ field }) => (
            <Stack gap={theme.spaces.x4} direction='horizontal' alignY='center'>
              <Switch {...field} checked={Boolean(field.value)} data-test-id='ldap-auth-start-tls-switch'>
                Start TLS
              </Switch>
              <Tooltip
                overlayStyle={overlayStyle}
                title='When connecting to the server, connect using the ldap:// protocol then issue a StartTLS command. If unspecified, connections will use the ldaps:// protocol.'>
                <div>
                  <InfoCircleIcon type='default' color={theme.colors.gray500} />
                </div>
              </Tooltip>
            </Stack>
          )}
          control={control}
          name='ldap.startTLS'
        />
      </Stack>
      <Stack direction='horizontal' align='space-between' alignY='center' gap={theme.spaces.x12}>
        <Controller
          render={({ field }) => (
            <StyledUploader showUploadList={true} maxFiles={1} onDrop={(files) => uploadFiles(files, field.onChange)}>
              <Stack gap={theme.spaces.x12} align='space-between' alignY='center' direction='horizontal'>
                <Button
                  icon={<LinkBrokenBIcon type='default' />}
                  variant='secondaryGray'
                  data-test-id='ldap-auth-root-certificate-button'>
                  Root Certificate
                </Button>
                <Tooltip overlayStyle={overlayStyle} placement='right' title='Trusted root certificate file.'>
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            </StyledUploader>
          )}
          control={control}
          name='ldap.rootCAData'
        />
        <Controller
          render={({ field }) => (
            <StyledUploader showUploadList={true} maxFiles={1} onDrop={(files) => uploadFiles(files, field.onChange)}>
              <Stack gap={theme.spaces.x12} align='space-between' alignY='center' direction='horizontal'>
                <Button
                  icon={<LinkBrokenBIcon type='default' />}
                  variant='secondaryGray'
                  data-test-id='ldap-auth-client-certificate-button'>
                  Client Certificate
                </Button>
                <Tooltip overlayStyle={overlayStyle} placement='right' title='Trusted client certificate file.'>
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            </StyledUploader>
          )}
          control={control}
          name='ldap.clientCertData'
        />
        <Controller
          render={({ field }) => (
            <StyledUploader showUploadList={true} maxFiles={1} onDrop={(files) => uploadFiles(files, field.onChange)}>
              <Stack gap={theme.spaces.x12} align='space-between' alignY='center' direction='horizontal'>
                <Button
                  icon={<LinkBrokenBIcon type='default' />}
                  variant='secondaryGray'
                  data-test-id='ldap-auth-client-key-button'>
                  Client Key
                </Button>
                <Tooltip overlayStyle={overlayStyle} placement='right' title='Trusted client key file.'>
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            </StyledUploader>
          )}
          control={control}
          name='ldap.clientKeyData'
        />
      </Stack>
      <Stack direction='horizontal' gap={theme.spaces.x12} align='space-between'>
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  * Bind Distinguished Name
                  <Tooltip
                    overlayStyle={overlayStyle}
                    title='The distinguished name for an application service account. The connector uses these credentials to search for users and groups. Prophecy does not store this credential.'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              placeholder='ou=People,dc=example,dc=org'
              {...field}
              variant={errors?.ldap?.bindDN ? 'error' : 'primary'}
              helpText={errors?.ldap?.bindDN?.message}
              data-test-id='ldap-auth-bind-distinguished-name-input'
            />
          )}
          control={control}
          name='ldap.bindDN'
          rules={{
            required: 'Bind bdn is required'
          }}
        />
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  * Bind Password
                  <Tooltip
                    overlayStyle={overlayStyle}
                    title='The distinguished password for an application service account. The connector uses these credentials to search for users and groups. Prophecy does not store this credential.'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              type='password'
              {...field}
              variant={errors?.ldap?.bindPW ? 'error' : 'primary'}
              helpText={errors?.ldap?.bindPW?.message}
              data-test-id='ldap-auth-bind-password-input'
            />
          )}
          control={control}
          name='ldap.bindPW'
          rules={{
            required: 'Bind password is required'
          }}
        />
      </Stack>
      <Controller
        render={({ field }) => (
          <StyledInputBox
            label={
              <Stack gap={theme.spaces.x4} direction='horizontal'>
                Username Prompt
                <Tooltip overlayStyle={overlayStyle} title='The attribute to display in the provided password prompt.'>
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            }
            placeholder='Email Address'
            {...field}
            variant={errors?.ldap?.usernamePrompt ? 'error' : 'primary'}
            helpText={errors?.ldap?.usernamePrompt?.message}
            data-test-id='ldap-auth-username-prompt-input'
          />
        )}
        control={control}
        name='ldap.usernamePrompt'
      />
      <Tooltip
        overlayStyle={overlayStyle}
        title='User search maps a username and password entered by a user to a LDAP entry.'>
        <StyledHeading level='sm15'>
          User Search <InfoCircleIcon type='default' color={theme.colors.gray500} />
        </StyledHeading>
      </Tooltip>
      <Stack gap={theme.spaces.x12} direction='horizontal' align='space-between'>
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  User Name
                  <Tooltip
                    overlayStyle={overlayStyle}
                    title='Username attribute used for comparing user entries. This will be translated and combined with the other filters like "(<attr>=<username>)".'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              placeholder='User Name'
              {...field}
              variant={errors?.ldap?.userSearch?.username ? 'error' : 'primary'}
              helpText={errors?.ldap?.userSearch?.username?.message}
              data-test-id='ldap-auth-user-search-username-input'
            />
          )}
          control={control}
          name='ldap.userSearch.username'
        />
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  Base Distinguished Name
                  <Tooltip
                    overlayStyle={overlayStyle}
                    title='BaseDN to start the search from. It will translate to the query "(&(objectClass=person)(uid=<username>))".'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              placeholder='ou=People,dc=example,dc=org'
              {...field}
              variant={errors?.ldap?.userSearch?.baseDN ? 'error' : 'primary'}
              helpText={errors?.ldap?.userSearch?.baseDN?.message}
              data-test-id='ldap-auth-user-search-base-distinguished-name-input'
            />
          )}
          control={control}
          name='ldap.userSearch.baseDN'
        />
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  Filter
                  <Tooltip overlayStyle={overlayStyle} title='Optional filter to apply when searching the directory.'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              placeholder='(objectClass=person)'
              {...field}
              variant={errors?.ldap?.userSearch?.filter ? 'error' : 'primary'}
              helpText={errors?.ldap?.userSearch?.filter?.message}
              data-test-id='ldap-auth-user-search-filter-input'
            />
          )}
          control={control}
          name='ldap.userSearch.filter'
        />
      </Stack>
      <Stack gap={theme.spaces.x12} direction='horizontal' align='space-between'>
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  ID Attribute
                  <Tooltip overlayStyle={overlayStyle} title='String representation of the user.'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              placeholder='DN'
              {...field}
              variant={errors?.ldap?.userSearch?.idAttr ? 'error' : 'primary'}
              helpText={errors?.ldap?.userSearch?.idAttr?.message}
              data-test-id='ldap-auth-user-search-id-attribute-input'
            />
          )}
          control={control}
          name='ldap.userSearch.idAttr'
        />
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  Email Attribute
                  <Tooltip overlayStyle={overlayStyle} title='Attribute to map to Email.'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              placeholder='mail'
              {...field}
              variant={errors?.ldap?.userSearch?.emailAttr ? 'error' : 'primary'}
              helpText={errors?.ldap?.userSearch?.emailAttr?.message}
              data-test-id='ldap-auth-user-search-email-attribute-input'
            />
          )}
          control={control}
          name='ldap.userSearch.emailAttr'
        />
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  Name Attribute
                  <Tooltip overlayStyle={overlayStyle} title='Maps to display name of users.'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              placeholder='cn'
              {...field}
              variant={errors?.ldap?.userSearch?.nameAttr ? 'error' : 'primary'}
              helpText={errors?.ldap?.userSearch?.nameAttr?.message}
              data-test-id='ldap-auth-user-search-name-attribute-input'
            />
          )}
          control={control}
          name='ldap.userSearch.nameAttr'
        />
      </Stack>
      <Tooltip overlayStyle={overlayStyle} title='Group search queries for groups given a user entry.'>
        <StyledHeading level='sm15'>
          Group Search <InfoCircleIcon type='default' color={theme.colors.gray500} />
        </StyledHeading>
      </Tooltip>
      <Stack gap={theme.spaces.x12} direction='horizontal' align='space-between'>
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  Name Attribute
                  <Tooltip overlayStyle={overlayStyle} title='Maps to display name of users.'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              placeholder='cn'
              {...field}
              variant={errors?.ldap?.groupSearch?.nameAttr ? 'error' : 'primary'}
              helpText={errors?.ldap?.groupSearch?.nameAttr?.message}
              data-test-id='ldap-auth-group-search-name-attribute-input'
            />
          )}
          control={control}
          name='ldap.groupSearch.nameAttr'
        />
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  Base Distinguished Name
                  <Tooltip
                    overlayStyle={overlayStyle}
                    title='BaseDN to start the search from. It will translate to the query "(&(objectClass=person)(uid=<username>))".'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              placeholder='ou=People,dc=example,dc=org'
              {...field}
              variant={errors?.ldap?.groupSearch?.baseDN ? 'error' : 'primary'}
              helpText={errors?.ldap?.groupSearch?.baseDN?.message}
              data-test-id='ldap-auth-group-search-base-distinguished-name-input'
            />
          )}
          control={control}
          name='ldap.groupSearch.baseDN'
        />
        <Controller
          render={({ field }) => (
            <StyledInputBox
              label={
                <Stack gap={theme.spaces.x4} direction='horizontal'>
                  Filter
                  <Tooltip overlayStyle={overlayStyle} title='Optional filter to apply when searching the directory.'>
                    <div>
                      <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    </div>
                  </Tooltip>
                </Stack>
              }
              placeholder='(objectClass=person)'
              {...field}
              variant={errors?.ldap?.groupSearch?.filter ? 'error' : 'primary'}
              helpText={errors?.ldap?.groupSearch?.filter?.message}
              data-test-id='ldap-auth-group-search-filter-input'
            />
          )}
          control={control}
          name='ldap.groupSearch.filter'
        />
      </Stack>
      <Stack gap={theme.spaces.x24}>
        <LabelWithHint
          level='sm'
          hint={`Following list contains field pairs that are used to match a user to a group. It adds an additional requirement to the filter that an attribute in the group must match the user's attribute value.`}>
          User Matchers
        </LabelWithHint>
        <UserAttribute tableData={userMatchers || []} updateUserMatchersData={(data) => updateUserMatchersData(data)} />
      </Stack>
    </ScrollableStack>
  );
}
